import { Column, Row } from "@/components";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { healthTierToHexColor, healthTierToLabel } from "@/lib/constants";
import { type PortfolioHealth, type PortfolioHealthScoreTier } from "@/types";

// Bar classes stay literal so Tailwind can compile the arbitrary values; hex
// values must match healthTierToHexColor.
const HEALTH_BAR_SEGMENTS = [
  {
    tierKey: "critical",
    label: healthTierToLabel.critical,
    color: healthTierToHexColor.critical,
    barClassName: "bg-[#D14343]/70 hover:bg-[#D14343] dark:hover:bg-[#D14343]",
    activeBarClassName: "bg-[#D14343]",
  },
  {
    tierKey: "at-risk",
    label: healthTierToLabel["at-risk"],
    color: healthTierToHexColor["at-risk"],
    barClassName: "bg-[#E67E22]/70 hover:bg-[#E67E22] dark:hover:bg-[#E67E22]",
    activeBarClassName: "bg-[#E67E22]",
  },
  {
    tierKey: "watch",
    label: healthTierToLabel.watch,
    color: healthTierToHexColor.watch,
    barClassName: "bg-[#F2C94C]/70 hover:bg-[#F2C94C] dark:hover:bg-[#F2C94C]",
    activeBarClassName: "bg-[#F2C94C]",
  },
  {
    tierKey: "healthy",
    label: healthTierToLabel.healthy,
    color: healthTierToHexColor.healthy,
    barClassName: "bg-[#27AE60]/70 hover:bg-[#27AE60] dark:hover:bg-[#27AE60]",
    activeBarClassName: "bg-[#27AE60]",
  },
] as const;

const HEALTH_BAR_SKELETON_SEGMENTS = [
  { label: "Critical", flexGrow: 0.45 },
  { label: "At risk", flexGrow: 1.3 },
  { label: "Watch", flexGrow: 1.45 },
  { label: "Healthy", flexGrow: 0.9 },
] as const;

export const PipelineHealthScoreCard = ({
  counts,
  isLoading,
  stafferName = null,
  salesName = null,
  onTierSelect,
  selectedTier = null,
}: {
  counts: PortfolioHealth;
  isLoading: boolean;
  stafferName?: string | null;
  salesName?: string | null;
  onTierSelect?: (tier: PortfolioHealthScoreTier) => void;
  selectedTier?: PortfolioHealthScoreTier | null;
}) => {
  const renderSkeletonSegments = () => {
    return HEALTH_BAR_SKELETON_SEGMENTS.map((segment) => (
      <Column
        key={segment.label}
        className="min-w-[8px] gap-2.5 overflow-hidden"
        style={{ flexGrow: segment.flexGrow, flexBasis: 0 }}
      >
        <Skeleton className="h-8 w-full min-w-0 rounded-full shadow-sm" />
        <Column className="min-w-0 gap-1 border-l-[3px] border-slate-200 pl-2">
          <Skeleton className="h-5 w-12 max-w-full" />
          <Skeleton className="h-3 w-16 max-w-full" />
        </Column>
      </Column>
    ));
  };

  const renderSegments = () => {
    if (isLoading) {
      return renderSkeletonSegments();
    }

    const total = HEALTH_BAR_SEGMENTS.reduce(
      (sum, segment) => sum + counts[segment.tierKey],
      0,
    );
    if (total === 0) {
      return <div>No data found</div>;
    }

    return HEALTH_BAR_SEGMENTS.map((segment) => {
      const count = counts[segment.tierKey];
      if (count === 0) {
        return null;
      }

      const isSelected = selectedTier === segment.tierKey;
      const percentage = total > 0 ? Math.round((count / total) * 100) : 0;

      return (
        <Column
          key={segment.tierKey}
          className="min-w-[76px] gap-2.5"
          style={{ flexGrow: count, flexBasis: 0 }}
        >
          <Button
            type="button"
            variant="ghost"
            aria-label={`Filter by ${segment.label}`}
            aria-pressed={isSelected}
            disabled={!onTierSelect}
            className={cn(
              "h-8 w-full min-w-0 cursor-pointer rounded-full p-0 shadow-sm ring-1 ring-slate-900/10",
              segment.barClassName,
              isSelected && segment.activeBarClassName,
            )}
            title={`${segment.label}: ${count.toLocaleString()}`}
            onClick={() => {
              onTierSelect?.(segment.tierKey);
            }}
          />
          <Button
            type="button"
            variant="ghost"
            aria-label={`Filter by ${segment.label}`}
            aria-pressed={isSelected}
            disabled={!onTierSelect}
            className={cn(
              "h-auto w-full min-w-0 cursor-pointer justify-start rounded-none border-l-[3px] p-0 hover:bg-slate-50/80",
              isSelected && "bg-slate-50",
            )}
            // `B3` ≈ 70% alpha (0.7 × 255) on the tier color
            style={{ borderColor: `${segment.color}B3` }}
            onClick={() => {
              onTierSelect?.(segment.tierKey);
            }}
          >
            <Column className="min-w-0 items-start gap-1 pl-2 text-left">
              <p
                className={
                  "text-left text-xl font-semibold tabular-nums leading-none text-slate-900/80"
                }
              >
                {count.toLocaleString()}
              </p>
              <p className="whitespace-nowrap text-left text-xs font-medium leading-tight">
                <span className="text-slate-500">{segment.label}</span>
                <span className="text-slate-500"> · </span>
                <span className="text-slate-900">{percentage}%</span>
              </p>
            </Column>
          </Button>
        </Column>
      );
    });
  };

  return (
    <Card className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-5 pb-6 pt-5 shadow-none">
      <Row className="mb-4 h-7 items-center justify-between gap-2">
        <Row className="min-w-0 items-baseline gap-2">
          <p className="text-base font-semibold text-slate-900">Health score</p>
          <p className="truncate text-xs text-slate-500">
            {stafferName
              ? `Across ${stafferName}'s pipelines`
              : salesName
                ? `Across ${salesName}'s jobs`
                : "Across all pipelines"}
          </p>
        </Row>
      </Row>

      <Row
        className="min-h-0 w-full min-w-0 flex-1 items-stretch justify-center gap-x-1.5"
        role="group"
      >
        {renderSegments()}
      </Row>
    </Card>
  );
};
